import React from 'react'
import {
  RecordLink,
  RecordsSelect
} from 'portal/components/RecordsTable/RecordsColumn'
import { icons } from 'portal/lib/primitives'
import { RECORD_STATUS } from 'portal/lib/constants'
import { recordsComponentFactory } from './recordsComponentFactory'
import { Input, Tooltip } from 'antd'

const { Search } = Input

function setCellId(record) {
  if (record.removed) {
    return {
      id: 'RecordsTableRow',
      style: {
        textDecoration: 'line-through',
        opacity: '0.5'
      }
    }
  }
  return {
    id: 'RecordsTableCell'
  }
}

export const Delivery = recordsComponentFactory({
  ModuleName: 'Delivery',
  dateField: 'deliveryDate',
  buildRecordsForDisplay: function (records, projectTimeZone) {
    return {
      data: (records ?? []).map(record => {
        const productGroupNames = record.data.products
          ?.reduce((pg, item) => {
            if (!item.isDeleted && !item.isRemoved && item.productGroupName) {
              pg.push(item.productGroupName)
            }

            // Remove duplicates and return
            return [...new Set([...pg])]
          }, [])
          .join(',\n')

        return {
          record: record,
          createdAt: new Date(record.createdAt).toLocaleDateString([], {
            timeZone: projectTimeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }),
          date: record.data.deliveryArrivalDate
            ? new Date(record.data.deliveryArrivalDate).toLocaleString([], {
                timeZone: projectTimeZone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              })
            : undefined,
          deliveryId: record.data.deliveryId,
          deliveryAddressKnown: record.data.deliveryAddressKnown,
          tradeContractor: record.data.tradeContractor,
          knownSupplierName: record.data.knownSupplierName,
          recordIdRaw: record.recordId,
          productGroupNames: productGroupNames,
          productsWithDesignMatchesCount: record.productsWithDesignMatchesCount,
          removed: record.status === RECORD_STATUS.REMOVED
        }
      })
    }
  },
  buildColumnsForTable: function (
    { size },
    state,
    fetchOptions,
    assignedToOptions,
    filters,
    updateFilters,
    projectId,
    tableId,
    accountId,
    sortById
  ) {
    return [
      {
        dataIndex: 'createdAt',
        className: 'createdAt',
        title: 'Created At',
        width: '8.125rem',
        onCell: setCellId
      },
      {
        dataIndex: 'date',
        className: 'arrivalDate',
        title: 'Arrival Date',
        width: '8.125rem',
        onCell: setCellId
      },
      {
        // Title is a function to prevent ellipsis from added a title tooltip to the header cell
        title: () => <div>Delivery ID</div>,
        dataIndex: 'deliveryId',
        className: 'deliveryId',
        sorter: true,
        ellipsis: true,
        filterDropdown: () => (
          <Search
            defaultValue={filters.deliveryId}
            placeholder={'Note: Case sensitive'}
            onSearch={value => {
              updateFilters('deliveryId', value)
            }}
            onBlur={e => {
              if (e.target.value !== filters.deliveryId) {
                updateFilters('deliveryId', e.target.value)
              }
            }}
            data-testid={'deliveryIdFilter'}
          />
        ),
        filtered: !!filters.deliveryId,
        onCell: setCellId
      },
      size > 1 && {
        // Title is a function to prevent ellipsis from added a title tooltip to the header cell
        title: () => <div>Delivery Address</div>,
        dataIndex: 'deliveryAddressKnown',
        className: 'deliveryAddressKnown',
        ellipsis: true,
        filterDropdown: () =>
          RecordsSelect(
            'deliveryAddressKnown',
            state,
            fetchOptions,
            filters,
            updateFilters,
            projectId,
            tableId
          ),
        filtered:
          filters.deliveryAddressKnown &&
          filters.deliveryAddressKnown.length > 0,
        filterDropdownProps: {
          onOpenChange(open) {
            if (open) {
              setTimeout(() => {
                const event = new MouseEvent('mousedown', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                })
                const selectorElement = document.querySelector(
                  `[name="deliveryAddressKnown"] .ant-select-selector`
                )
                if (selectorElement) {
                  selectorElement.dispatchEvent(event)
                  selectorElement.click()
                }
              }, 100)
            }
          }
        },
        onCell: setCellId
      },
      size > 1 && {
        // Title is a function to prevent ellipsis from added a title tooltip to the header cell
        title: () => <div>Trade Contractor</div>,
        ellipsis: true,
        filterDropdown: () =>
          RecordsSelect(
            'tradeContractor',
            state,
            fetchOptions,
            filters,
            updateFilters,
            projectId,
            tableId
          ),
        filtered: filters.tradeContractor && filters.tradeContractor.length > 0,
        filterDropdownProps: {
          onOpenChange(open) {
            if (open) {
              setTimeout(() => {
                const event = new MouseEvent('mousedown', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                })
                const selectorElement = document.querySelector(
                  `[name="tradeContractor"] .ant-select-selector`
                )
                if (selectorElement) {
                  selectorElement.dispatchEvent(event)
                  selectorElement.click()
                }
              }, 100)
            }
          }
        },
        dataIndex: 'tradeContractor',
        className: 'tradeContractor',
        onCell: setCellId
      },
      size > 2 && {
        // Title is a function to prevent ellipsis from added a title tooltip to the header cell
        title: () => <div>Supplier Company</div>,
        ellipsis: true,
        filterDropdown: () =>
          RecordsSelect(
            'knownSupplierName',
            state,
            fetchOptions,
            filters,
            updateFilters,
            projectId,
            tableId
          ),
        filtered:
          filters.knownSupplierName && filters.knownSupplierName.length > 0,
        filterDropdownProps: {
          onOpenChange(open) {
            if (open) {
              setTimeout(() => {
                const event = new MouseEvent('mousedown', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                })
                const selectorElement = document.querySelector(
                  `[name="knownSupplierName"] .ant-select-selector`
                )
                if (selectorElement) {
                  selectorElement.dispatchEvent(event)
                  selectorElement.click()
                }
              }, 100)
            }
          }
        },
        dataIndex: 'knownSupplierName',
        className: 'knownSupplierName',
        onCell: setCellId
      },
      size > 1 && {
        // Title is a function to prevent ellipsis from added a title tooltip to the header cell
        title: () => <div>Product Groups</div>,
        ellipsis: true,
        filterDropdown: () =>
          RecordsSelect(
            'products[].productGroupName',
            state,
            fetchOptions,
            filters,
            updateFilters,
            projectId,
            tableId
          ),
        filtered:
          filters['products[].productGroupName'] &&
          filters['products[].productGroupName'].length > 0,
        filterDropdownProps: {
          onOpenChange(open) {
            if (open) {
              setTimeout(() => {
                const event = new MouseEvent('mousedown', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                })
                const selectorElement = document.querySelector(
                  `[name="products[].productGroupName"] .ant-select-selector`
                )
                if (selectorElement) {
                  selectorElement.dispatchEvent(event)
                  selectorElement.click()
                }
              }, 100)
            }
          }
        },
        dataIndex: 'productGroupNames',
        className: 'productGroupNames',
        onCell: setCellId
      },
      size > 1 && {
        title: 'Assigned To',
        ellipsis: false,
        dataIndex: 'assignedTo',
        className: 'assignedTo',
        filterDropdown: () =>
          RecordsSelect(
            'assignedTo',
            state,
            assignedToOptions,
            filters,
            updateFilters,
            projectId,
            tableId
          ),
        filtered: filters.assignedTo && filters.assignedTo.length > 0,
        filterDropdownProps: {
          onOpenChange(open) {
            if (open) {
              setTimeout(() => {
                const event = new MouseEvent('mousedown', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                })
                const selectorElement = document.querySelector(
                  `[name="assignedTo"] .ant-select-selector`
                )
                if (selectorElement) {
                  selectorElement.dispatchEvent(event)
                  selectorElement.click()
                }
              }, 100)
            }
          }
        },
        width: '8rem',
        align: 'center'
      },
      {
        dataIndex: 'productsWithDesignMatchesCount',
        className: 'productsWithDesignMatchesCount',
        title: (
          <Tooltip title="Number of products with design matches for this record">
            <icons.DesignMatch alt="Design Matches" />
          </Tooltip>
        ),
        width: '3rem',
        align: 'center',
        render: (num, record, index) => {
          if (num === 0) {
            return null
          }
          const className = record.hasMaterialWatchlistAlert
            ? ' record-alert-cell'
            : ''
          return (
            <Tooltip
              title={className ? 'Record has material watchlist alert' : ''}
            >
              <div
                className={`design-matches-count${className}`}
                data-testid="designMatchesCountCell"
              >
                {num}
              </div>
            </Tooltip>
          )
        }
      },
      RecordLink(setCellId, filters, updateFilters)
    ]
  },
  initialFilterState: {
    'deliveryId': '',
    'deliveryDate': { startDate: undefined, endDate: undefined },
    'deliveryAddressKnown': [],
    'tradeContractor': [],
    'knownSupplierName': [],
    'startDate': undefined,
    'endDate': undefined,
    'products[].productGroupName': []
  }
})
